<template>
  <div class="bg-white">
    <div
      class="row mx-0 calendar-header"
      :class="{ 'calendar-header-month': type === 'month' }"
    >
      <div class="col d-flex justify-content-start">
        <RsButton
          class="mx-2"
          variant="secondary"
          hollow
          @click="setToday"
        >
          {{ todayLabel }}
        </RsButton>
        <div v-if="hasDefaultSyncButton">
          <RsButton
            variant="secondary"
            icon="sync-alt"
            @click="handleSyncButtonClick()"
          >
            Sync
          </RsButton>
          <RsIcon
            id="sync"
            weight="fas"
            class="rs-calendar__sync ml-2"
            icon="question-circle"
          />
          <RsTooltip
            target="sync"
            title="This will sync your calendar"
            position="right"
            :delay="{ show: 200 }"
          />
        </div>
        <slot name="customSyncButton" />
      </div>
      <div class="col d-flex justify-content-center align-items-center">
        <RsNavigationButton
          id="previous"
          icon="arrow-square-left"
          description="Previous"
          @click="prev"
        />
        <span
          v-if="calendarTitle"
          class="mx-2 rs-calendar__title"
        >{{ calendarTitle }}</span>
        <RsNavigationButton
          id="next"
          @click="next"
        />
      </div>
      <div class="col d-flex justify-content-end mr-2">
        <RsButton
          variant="secondary"
          :hollow="type !== 'day'"
          class="btn-no-border-right"
          @click="getViewType('day')"
        >
          {{ dayLabel }}
        </RsButton>
        <RsButton
          variant="secondary"
          :hollow="type !== 'week'"
          class="btn-no-border"
          @click="getViewType('week')"
        >
          {{ weekLabel }}
        </RsButton>
        <RsButton
          variant="secondary"
          :hollow="type !== 'month'"
          class="btn-no-border-left"
          @click="getViewType('month')"
        >
          {{ monthLabel }}
        </RsButton>
      </div>
    </div>
    <div :style="[type !== 'month' ? { height: height } : {}, boxHeightCSS]">
      <FullCalendar
        ref="calendar"
        class="mt-2 rs-calendar"
        :options="calendarOptions"
      >
        <template #dayHeaderContent="{ date, isToday, text }">
          <span v-if="type === 'month'">{{ text }}</span>
          <div
            v-else
            class="d-flex align-items-center justify-content-center"
          >
            <span>{{ formatHeaderWeekday(date) }}</span>
            <span
              class="rs-calendar-header-day-number ml-1"
              :class="{ 'rs-calendar-header-day-number--today': isToday }"
            >{{ formatHeaderDayNumber(date) }}</span>
          </div>
        </template>
        <template #eventContent="{ event }">
          <div
            :style="event.extendedProps.eventBlockCustomClass"
            :class="{
              'event-default': event.extendedProps.type === 'tour',
              'event-exception': event.extendedProps.type === 'exception',
              'event-clickable': isUseDefaultEventModal,
            }"
            class="pl-2 event-block"
          >
            <div class="font-weight-bold rs-calendar-event--truncate">
              {{ event.title }}
            </div>
            <div class="rs-calendar-event--truncate">
              {{ formatStartEndTime(event.extendedProps._original) }}
            </div>
          </div>
        </template>
      </FullCalendar>
    </div>
    <RsCalendarEventModal
      v-if="isUseDefaultEventModal"
      :show-event-modal="showEventModal"
      :event-in-view="eventInView"
      @on-modal-close="handleEventModalClose"
      @on-edit-event-block="handleEventBlockEdit"
      @on-delete-event-block="handleEventBlockDelete"
      @on-show-event-modal="handleShowEventModal"
    >
      <template #event-details="slotProps">
        <slot
          name="event-details"
          :details="slotProps.details"
        />
      </template>
    </RsCalendarEventModal>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import FullCalendar from '@fullcalendar/vue3';
import type {
  CalendarApi, CalendarOptions, DatesSetArg, EventClickArg, EventInput,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import dayjs from 'dayjs';
import { ICalendarEvent } from '@/shared/interfaces/calendar-event';

export type TCalendarEmitData = {
  events: ICalendarEvent[],
  event: ICalendarEvent,
};

type TCalendarView = 'month' | 'week' | 'day';

// Maps RsCalendar's public view names onto FullCalendar's internal view names.
const VIEW_MAP: Record<TCalendarView, string> = {
  month: 'dayGridMonth',
  week: 'timeGridWeek',
  day: 'timeGridDay',
};

// Module-level so calendarOptions doesn't mint a new array identity on every computed
// re-evaluation — FullCalendar treats changed options as a reset and re-fires datesSet,
// which can feed an event-refetch loop in consumers.
const CALENDAR_LOCALES = [frLocale];

export default defineComponent({
  name: 'Calendar',

  components: { FullCalendar },

  props: {
    /**
     * Boolean to indicate whether to show/hide sync button
     */
    hasDefaultSyncButton: {
      type: Boolean,
      default: true,
    },
    /**
     * List of event objects to display in the calendar
     */
    events: {
      type: Array as PropType<ICalendarEvent[]>,
      default: () => [],
    },
    /**
     * Additional calendar details that may be useful for
     * syncing the calendar
     */
    calendarDetails: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },
    /**
     * Boolean to indicate whether to show default event modal
     * upon event block click
     */
    isUseDefaultEventModal: {
      type: Boolean,
      default: true,
    },

    /**
     * String representing the height of the calendar in
     * week and day calendar
     */
    height: {
      type: String,
      default: 'auto',
    },

    /**
     * String representing the min height of the
     * calendar box
     */
    boxMinHeight: {
      type: Number,
    },

    /**
     * String representing the min height of the
     * calendar box
     */
    firstTime: {
      type: String,
      default: '00:00',
    },

    /**
     * Label for the "Today" navigation button
     */
    todayLabel: {
      type: String,
      default: 'Today',
    },

    /**
     * Label for the "Day" view toggle button
     */
    dayLabel: {
      type: String,
      default: 'Day',
    },

    /**
     * Label for the "Week" view toggle button
     */
    weekLabel: {
      type: String,
      default: 'Week',
    },

    /**
     * Label for the "Month" view toggle button
     */
    monthLabel: {
      type: String,
      default: 'Month',
    },

    /**
     * Label for the link shown when a day (month view) or time slot
     * (week/day view) has more events than maxEventsPerSlot
     */
    showMoreLabel: {
      type: String,
      default: 'Show More',
    },

    /**
     * Max number of event blocks to show in a day cell (month view) or
     * time slot (week/day view) before collapsing the rest behind a
     * "Show More" link/popover. Unset means no cap — opt in per consumer.
     */
    maxEventsPerSlot: {
      type: Number,
    },

  },

  emits: [
    'on-show-event-click',
    'on-date-range-change',
    'on-edit-event',
    'on-delete-event',
    'on-sync-button-click',
    'on-show-event-modal',
  ],

  data() {
    return {
      type: 'month' as TCalendarView,
      showEventModal: false as boolean,
      eventInView: {} as ICalendarEvent,
      calendarTitle: '' as string,
    };
  },
  computed: {
    /**
     * FullCalendar events derived from the ICalendarEvent list. The original
     * event is preserved in extendedProps so click handlers and the custom
     * event slot can read all of RsCalendar's domain fields.
     */
    mappedEvents(): EventInput[] {
      return this.events.map((event) => ({
        title: event.name,
        start: event.start ? dayjs(`${event.start}`).toDate() : undefined,
        end: event.end ? dayjs(`${event.end}`).toDate() : undefined,
        extendedProps: {
          type: event.type,
          eventBlockCustomClass: event.eventBlockCustomClass,
          _original: event,
        },
      }));
    },
    calendarOptions(): CalendarOptions {
      return {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        locales: CALENDAR_LOCALES,
        locale: this.$i18n.locale,
        initialView: VIEW_MAP[this.type],
        headerToolbar: false,
        events: this.mappedEvents,
        eventOverlap: true,
        slotEventOverlap: false,
        slotMinTime: `${this.firstTime}:00`,
        allDaySlot: false,
        firstDay: 0,
        // Collapse extra event blocks behind a "Show More" link/popover once a day cell
        // (month view) or time slot (week/day view) exceeds maxEventsPerSlot. Only consumers
        // that opt in via the prop get this cap — others keep FullCalendar's default of none.
        ...(this.maxEventsPerSlot !== undefined ? {
          dayMaxEvents: this.maxEventsPerSlot,
          eventMaxStack: this.maxEventsPerSlot,
          moreLinkContent: () => this.showMoreLabel,
        } : {}),
        // Month view grows to fit; week/day fill the fixed-height wrapper and scroll.
        height: this.type === 'month' ? 'auto' : '100%',
        expandRows: true,
        nowIndicator: true,
        dayHeaderFormat: this.type === 'week'
          ? { weekday: 'long', day: 'numeric', omitCommas: true }
          : { weekday: 'long' },
        datesSet: this.updateRange,
        eventClick: this.showEvent,
        dateClick: this.viewDay,
      };
    },
    boxHeightCSS(): Record<string, string> {
      return {
        '--box-min-height-day': `${this.boxMinHeight}px`,
        '--box-min-height-week': `${(this.boxMinHeight ?? 0) + 20}px`,
      };
    },
  },

  methods: {
    calendarApi(): CalendarApi {
      return (this.$refs.calendar as InstanceType<typeof FullCalendar>).getApi();
    },
    formatStartEndTime(event: ICalendarEvent): string {
      return `${dayjs(`${event.start}`).format('h:mm a')}${event.start && event.end ? '-' : ''} ${dayjs(`${event.end}`).format('h:mm a')}`;
    },
    formatHeaderWeekday(date: Date): string {
      return dayjs(date).locale(this.$i18n.locale).format('dddd');
    },
    formatHeaderDayNumber(date: Date): string {
      return dayjs(date).format('D');
    },
    formatToFullDate(date: string | Date): string {
      return dayjs(`${date}`).locale(this.$i18n.locale).format('MMMM D, YYYY');
    },
    formatToYear(date: string | Date): string {
      return dayjs(`${date}`).format('YYYY');
    },
    formatToMonth(date: string | Date): string {
      return dayjs(`${date}`).locale(this.$i18n.locale).format('MMMM');
    },
    formatToDayYear(date: string | Date): string {
      return dayjs(`${date}`).format('D, YYYY');
    },
    formatToMonthDay(date: string | Date): string {
      return dayjs(`${date}`).locale(this.$i18n.locale).format('MMMM D');
    },
    formatCalendarWeekTitle(startDate: string | Date, endDate: string | Date): string {
      let title = `${this.formatToMonthDay(startDate)}-${this.formatToDayYear(endDate)}`;
      if (`${this.formatToYear(startDate)}` !== `${this.formatToYear(endDate)}`) title = `${this.formatToFullDate(startDate)} - ${this.formatToFullDate(endDate)}`;
      if (`${this.formatToMonth(startDate)}` !== `${this.formatToMonth(endDate)}`) title = `${this.formatToMonthDay(startDate)} - ${this.formatToFullDate(endDate)}`;
      return title;
    },
    getViewType(type: TCalendarView): void {
      this.type = type;
      this.calendarApi().changeView(VIEW_MAP[type]);
    },
    viewDay(arg: { date: Date }): void {
      this.type = 'day';
      this.calendarApi().changeView(VIEW_MAP.day, arg.date);
    },
    setToday(): void {
      this.calendarApi().today();
    },
    prev(): void {
      this.calendarApi().prev();
    },
    next(): void {
      this.calendarApi().next();
    },
    showEvent(arg: EventClickArg): void {
      const event = arg.event.extendedProps._original as ICalendarEvent;
      if (this.isUseDefaultEventModal) this.showEventModal = true;
      this.eventInView = event;
      this.$emit('on-show-event-click', event);
    },
    handleEventModalClose(): void {
      if (this.isUseDefaultEventModal) this.showEventModal = false;
      this.eventInView = {} as ICalendarEvent;
    },
    /**
     * Fired by FullCalendar whenever the visible date range changes (view
     * switch, prev/next, today). `arg.start`/`arg.end` span the full visible
     * range with `end` exclusive, matching the min/max window the previous
     * Vuetify implementation emitted. Titles use the current period bounds.
     */
    updateRange(arg: DatesSetArg): void {
      const periodStart = arg.view.currentStart;
      // currentEnd is exclusive; the last visible day is one day earlier.
      const periodEndInclusive = dayjs(arg.view.currentEnd).subtract(1, 'day').toDate();
      if (this.type === 'day') this.calendarTitle = this.formatToFullDate(periodStart);
      else if (this.type === 'week') this.calendarTitle = this.formatCalendarWeekTitle(periodStart, periodEndInclusive);
      else this.calendarTitle = dayjs(periodStart).locale(this.$i18n.locale).format('MMMM YYYY');
      this.$emit('on-date-range-change', { min: arg.start, max: arg.end });
    },
    handleEventBlockEdit(): void {
      this.$emit('on-edit-event', { events: this.events, event: this.eventInView });
    },
    handleEventBlockDelete(): void {
      this.$emit('on-delete-event', { events: this.events, event: this.eventInView });
      this.showEventModal = false;
    },
    handleSyncButtonClick(): void {
      this.$emit('on-sync-button-click', { data: this.calendarDetails });
    },
    handleShowEventModal(): void {
      this.$emit('on-show-event-modal', { event: this.eventInView });
    },
  },
});
</script>
<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.calendar-header {
  background-color: white;
  margin-left: 1px !important;
  padding-top: 4px;
}
.calendar-header-month {
  position: sticky !important;
  top: 0;
  z-index: 4;
}
.event-default {
  background-color: #E6F3FA;
  border-left: 5px solid $vibrant-blue;
  border-top: 1px solid $vibrant-blue;
  border-right: 1px solid $vibrant-blue;
  border-bottom: 1px solid $vibrant-blue;
}
.event-exception {
  background-color: #B9B9B9;
  border-left: 5px solid #0D0D0D;
  border-top: 1px solid #0D0D0D;
  border-right: 1px solid #0D0D0D;
  border-bottom: 1px solid #0D0D0D;
}
.event-block {
  padding-top: 4px;
  padding-bottom: 4.5px;
  border-radius: 3px;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  color: $black;
  font-size: 12px;
}
.event-clickable {
  cursor: pointer;
}
.rs-calendar {
  &__sync {
    color: $secondary-color;
  }
  &__arrow {
    width: 16px;
    font-weight: 400;
    height: 16px;
    color: $secondary-color
  }
  &__title {
    color: $secondary-color-dark;
    font-size: 22px;
    font-family: $body-font-family;
    font-weight: bold;
    color: $secondary-color-dark;
    white-space: nowrap;
  }
  &-event {
    &--truncate {
      width: 100%;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      line-height: 1.3;
    }
  }
}

// FullCalendar renders each event inside its own anchor/frame; strip the
// library's default chrome so the custom event block controls all styling.
:deep(.fc-event),
:deep(.fc-event:hover),
:deep(.fc-h-event),
:deep(.fc-v-event) {
  border: none;
  background: transparent;
  box-shadow: none;
  overflow: hidden;
}
:deep(.fc-event .fc-event-main) {
  padding: 0;
  color: inherit;
}
:deep(.fc-daygrid-event-harness),
:deep(.fc-timegrid-event-harness) {
  margin: 1px 0;
}

// Weekday header styling to mirror the previous calendar.
:deep(.fc .fc-col-header-cell) {
  border-bottom: 1px solid rgba(0, 0, 0, .19);
  background-color: white;
}
:deep(.fc .fc-col-header-cell-cushion) {
  font-weight: bold;
  color: #2C2254;
  font-size: 14px;
  text-transform: capitalize;
  padding: 8px 4px;
}

// Highlight today with the brand colour, echoing the previous "present" style.
// Only the day number itself is highlighted, not the whole cell/column.
:deep(.fc .fc-day-today) {
  background-color: transparent;
}
:deep(.fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number) {
  background-color: $primary-color;
  color: $white;
  border-radius: 10%;
}
.rs-calendar-header-day-number {
  padding: 2px 6px;
  &--today {
    background-color: $primary-color;
    color: $white;
    border-radius: 10%;
  }
}

:deep(.fc-daygrid-day-number) {
  color: $black;
}

// Minimum row heights driven by the boxMinHeight prop.
:deep(.fc .fc-daygrid-day-frame) {
  min-height: var(--box-min-height-day);
}

:deep(.fc-timegrid-slot-label-cushion) {
  font-size: 12px;
}

// "Show More" link and its popover of the collapsed event blocks.
:deep(.fc-more-link) {
  color: $primary-color;
  font-weight: bold;
  font-size: 11px;
}
:deep(.fc-popover) {
  border: none;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.30);
}
:deep(.fc-popover-header) {
  background-color: white;
  color: #2C2254;
  font-weight: bold;
  border-radius: 10px;
}

.btn-no-border {
  border-radius: 0px;
}
.btn-no-border-left {
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
}
.btn-no-border-right {
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
}

</style>
